/**
 * The subscription plan for a voice room.
 *
 * THIS IS NOT OPTIONAL WIRING. `applyPlan` unsubscribes anything absent from
 * the plan, so a voice room that never sends one is a room where nobody can
 * hear anybody. The plan is recomputed on every roster change and handed to the
 * same density machinery the meeting grid uses.
 *
 * AUDIO: every remote participant is offered; `rankedAudio` then keeps the
 * loudest N (3 on `standard`, 5 is the server-side ceiling mirrored in
 * livekit.yaml). That is the capacity model's decision, not ours — and it is
 * defensible here because more than three people talking at once is not a
 * conversation.
 *
 * SCREEN SHARE: NOT subscribed until the viewer asks to watch. A room where six
 * people are chatting while one shares an IDE should not be paying for six
 * copies of that IDE, and "Watch" is a single click away.
 */
import type { MediaLayer } from '@cyphex/types';
import type { MediaClient, SubscriptionPlan } from '@/features/meeting/media/contract';

/**
 * Cameras in a voice room render as small circles, not a spotlight. 360p is the
 * top rung any of them can use; the profile ceiling clamps it further under
 * load.
 */
const CAMERA_LAYER: MediaLayer = 'l1_360p';

export function buildVoicePlan(client: MediaClient, watching: boolean): SubscriptionPlan {
  const snapshot = client.getSnapshot();
  const video = new Map<string, MediaLayer>();
  const audio: string[] = [];

  for (const participant of snapshot.participants) {
    if (participant.isLocal) continue;
    audio.push(participant.id);
    if (participant.cameraOn) video.set(participant.id, CAMERA_LAYER);
  }

  return {
    video,
    audio,
    screenShare: watching && snapshot.screenShareOwnerId !== null,
  };
}

export function applyVoicePlan(client: MediaClient, watching: boolean): void {
  client.applySubscriptions(buildVoicePlan(client, watching));
}
